import React, { useState, useMemo } from "react";
import { FaSearch, FaArrowRight, FaMapMarkedAlt } from "react-icons/fa";
import DropdownSelect from "./DropdownSelect";
import NW from "../../assets/npc/NW.jpg";
import NE from "../../assets/npc/NE.jpg";
import NC from "../../assets/npc/NC.jpg";
import SE from "../../assets/npc/SE.jpg";
import SS from "../../assets/npc/SS.jpg";
import SW from "../../assets/npc/SW.jpg";

const ZONE_IMAGES = {
  "North West": NW,
  "North East": NE,
  "North Central": NC,
  "South East": SE,
  "South South": SS,
  "South West": SW,
};

const SORT_OPTIONS = ["Alphabetical", "Achievement", "Registered"];

function getStatus(achievement) {
  if (achievement >= 70) return "HIGH PERFORMANCE";
  if (achievement >= 40) return "ON TRACK";
  return "ATTENTION REQUIRED";
}

const STATUS_CONFIG = {
  "HIGH PERFORMANCE": { color: "#006B3F", badgeBg: "#e8f5e9", iconBg: "#006B3F1A" },
  "ON TRACK": { color: "#d97706", badgeBg: "#fef3c7", iconBg: "#fef3c7" },
  "ATTENTION REQUIRED": { color: "#dc2626", badgeBg: "#fee2e2", iconBg: "#fee2e2" },
};


function ZoneCardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden animate-pulse">
      <div className="bg-gray-200" style={{ height: "200px" }} />
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-gray-200" style={{ width: "36px", height: "36px" }} />
            <div className="h-5 bg-gray-200 rounded w-28" />
          </div>
          <div className="h-4 bg-gray-200 rounded w-20" />
        </div>
        <div className="grid grid-cols-2 gap-y-4 mb-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i}>
              <div className="h-2 bg-gray-200 rounded w-16 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-20" />
            </div>
          ))}
        </div>
        <div className="h-2 bg-gray-200 rounded w-full mt-2" />
      </div>
    </div>
  );
}

function ZoneCard({ zone, onViewStates }) {
  const status = getStatus(zone.achievement);
  const { color, badgeBg, iconBg } = STATUS_CONFIG[status];

  return (
    <div className="flex flex-col h-full gap-1">
      <div className="bg-white rounded-xl border shadow-sm flex flex-col" style={{ borderColor: "#E2E8F0" }}>
        <div className="p-4 flex-1">
          <img
            src={ZONE_IMAGES[zone.name]}
            alt={zone.name}
            style={{
              height: "200px",
              width: "100%",
              objectFit: "cover",
              borderTopLeftRadius: "8px",
              borderTopRightRadius: "8px",
            }}
            className="mb-4"
          />
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg p-2 flex items-center justify-center" style={{ background: iconBg }}>
                <FaMapMarkedAlt style={{ color, fontSize: "20px" }} />
              </div>
              <span className="font-bold text-gray-900" style={{ fontSize: "18px" }}>
                {zone.name}
              </span>
            </div>
            <span
              className="font-bold"
              style={{ color, background: badgeBg, padding: "2px 8px", borderRadius: "4px", fontSize: "10px" }}
            >
              {status}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-y-4 mb-1">
            <div>
              <p className="font-bold mb-1 mt-0" style={{ color: "#3E4A41", fontSize: "10px" }}>TARGET</p>
              <p className="font-bold text-gray-900 mb-0 mt-0" style={{ fontSize: "16px" }}>
                {zone.target.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="font-bold mb-1 mt-0" style={{ color: "#3E4A41", fontSize: "10px" }}>REGISTERED</p>
              <p className="font-bold mb-0 mt-0" style={{ fontSize: "16px", color }}>
                {zone.registered.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="font-bold mb-1 mt-0" style={{ color: "#3E4A41", fontSize: "10px" }}>ACTIVE REGISTRARS</p>
              <p className="font-bold text-gray-900 mb-0 mt-0" style={{ fontSize: "16px" }}>
                {zone.activeRegistrars.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="font-bold mb-1 mt-0" style={{ color: "#3E4A41", fontSize: "10px" }}>ACHIEVEMENT</p>
              <p className="font-bold mb-0 mt-0" style={{ fontSize: "16px", color }}>
                {zone.achievement}%
              </p>
            </div>
          </div>

          <div className="mb-2">
            <p className="text-xs text-gray-500 mb-2 mt-0">Registration Progress</p>
            <div className="h-2 rounded-full bg-gray-100">
              <div
                className="h-2 rounded-full"
                style={{ width: `${Math.min(zone.achievement, 100)}%`, background: color, transition: "width 0.4s ease" }}
              />
            </div>
          </div>
        </div>
      </div>
      <button
        onClick={() => onViewStates(zone.name)}
        className="w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold"
        style={{
          color: "#01903C",
          background: "transparent",
          cursor: "pointer",
          borderRadius: "8px",
          border: "2px solid #D4E9D8",
        }}
      >
        View States <FaArrowRight style={{ fontSize: "12px" }} />
      </button>
    </div>
  );
}

export default function GeopoliticalZones({ zones = [], loading, onViewStates }) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("Alphabetical");

  const filtered = useMemo(() => {
    let list = zones.filter((z) => z.name.toLowerCase().includes(search.toLowerCase()));
    if (sort === "Alphabetical") list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    if (sort === "Achievement") list = [...list].sort((a, b) => b.achievement - a.achievement);
    if (sort === "Registered") list = [...list].sort((a, b) => b.registered - a.registered);
    return list;
  }, [zones, search, sort]);

  return (
    <div>
      {/* Search + Sort */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 mb-5">
        <div
          className="flex items-center gap-2 flex-1 bg-white border border-gray-200 rounded-lg px-4"
          style={{ height: "56px" }}
        >
          <FaSearch style={{ color: "#9ca3af", fontSize: "14px", flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Search Zone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 text-sm text-gray-700 bg-transparent"
            style={{ border: "none", outline: "none", height: "100%" }}
          />
        </div>
        <DropdownSelect
          label="Sort By"
          value={sort}
          onChange={setSort}
          options={SORT_OPTIONS}
        />
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {Array.from({ length: 6 }).map((_, i) => <ZoneCardSkeleton key={i} />)}
        </div>
      ) : filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {filtered.map((zone) => (
            <ZoneCard key={zone.id} zone={zone} onViewStates={onViewStates} />
          ))}
        </div>
      ) : (
        <p className="text-gray-400 text-sm text-center py-12 mb-0">No zones match your search.</p>
      )}
    </div>
  );
}
